<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useHotelManagementStore } from '@/store/HotelManagementStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useCruiseStore } from '@/store/CruiseStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Card from 'primevue/card';
import InputTextAera from 'primevue/textarea';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import City from '@/components/city/City.vue';

const { t } = useI18n({ useScope: 'global' });

const toast = useToast();
const hotelManagementStore = useHotelManagementStore();
const airManagementStore = useAirManagementStore();
const cruiseStore = useCruiseStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const isLoadingHotel = computed(() => hotelManagementStore.doLoading);
const isLoadingAir = computed(() => airManagementStore.doLoading);
const isLoadingCruise = computed(() => cruiseStore.doLoading);
const isLoadingFileJson = ref(false);
const isShowJsonFile = ref(false);
const jsonValue = ref('');

const syncData = async (type: string) => {
  isShowJsonFile.value = false;
  if (type == 'HOTEL') {
    hotelManagementStore
      .syncData()
      .then((data) => {
        if (data) {
          toast.add({ severity: 'success', summary: '', detail: t('notification.synch_success'), life: 3000 });
        }
      })
      .catch((err) => {
        toast.add({ severity: 'error', summary: '', detail: err?.message, life: 5000 });
      });
  } else if (type == 'AIRPORT') {
    airManagementStore
      .syncData()
      .then((data) => {
        if (data) {
          toast.add({ severity: 'success', summary: '', detail: t('notification.synch_success'), life: 3000 });
        }
      })
      .catch((err) => {
        toast.add({ severity: 'error', summary: '', detail: err?.message, life: 5000 });
      });
  } else if (type == 'CRUISE') {
    cruiseStore
      .syncData()
      .then((data) => {
        if (data) {
          toast.add({ severity: 'success', summary: '', detail: t('notification.synch_success'), life: 3000 });
        }
      })
      .catch((err) => {
        toast.add({ severity: 'error', summary: '', detail: err?.message, life: 5000 });
      });
  } else if (type == 'MAP_PACKAGE_DESTINATION') {
    isLoadingFileJson.value = true;
    isShowJsonFile.value = true;
    hotelManagementStore
      .getFileJson()
      .then((data) => {
        jsonValue.value = JSON.stringify(data?.data, null, '\t');
      })
      .catch((err) => {
        toast.add({ severity: 'error', summary: '', detail: err?.message, life: 5000 });
      })
      .finally(() => {
        isLoadingFileJson.value = false;
      });
  }
};

const handleUpdateFileJson = async (value) => {
  isLoadingFileJson.value = true;
  hotelManagementStore
    .updateFileJson(value)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.synch_success'), life: 3000 });
    })
    .catch((err) => {
      toast.add({ severity: 'error', summary: '', detail: err?.message, life: 5000 });
    })
    .finally(() => {
      isLoadingFileJson.value = false;
    });
};
</script>
<template>
  <LoadingSpinner v-if="isLoadingAir || isLoadingHotel || isLoadingCruise || isLoadingFileJson" />
  <Card class="card col-12">
    <template #title>
      <h4>{{ $t('management.content_management_update') }}</h4>
    </template>
    <template #content>
      <Tabs :value="0">
        <TabList>
          <Tab :key="0" :value="0">{{ $t('management.data_synchronization') }}</Tab>
          <Tab :key="1" :value="1">{{ $t('management.gate_des_region_refer') }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel :key="0" :value="0">
            <div class="flex justify-content-between flex-col sm:flex-row">
              <div class="flex justify-content-left flex-col sm:flex-row gap-3">
                <Button
                  :disabled="!authorizations?.configurationPage?.data_synchronization_active"
                  type="button"
                  icon="pi pi-sync"
                  :label="$t('management.synchronize_package_and_hotel')"
                  data-testid="btn-sync-hotel"
                  class="mb-2"
                  severity="success"
                  @click="syncData('HOTEL')"
                />
                <Button
                  :disabled="!authorizations?.configurationPage?.data_synchronization_active"
                  type="button"
                  icon="pi pi-sync"
                  :label="t('management.synchronize_airport')"
                  data-testid="btn-sync-airport"
                  class="mb-2 hidden"
                  severity="success"
                  @click="syncData('AIRPORT')"
                />
                <Button
                  :disabled="!authorizations?.configurationPage?.data_synchronization_active"
                  type="button"
                  icon="pi pi-sync"
                  :label="$t('management.synchronize_cruise')"
                  data-testid="btn-sync-cruise"
                  class="mb-2"
                  severity="success"
                  @click="syncData('CRUISE')"
                />
                <Button
                  :disabled="!authorizations?.configurationPage?.data_synchronization_active"
                  type="button"
                  icon="pi pi-sync"
                  :label="$t('management.map_package_destination')"
                  data-testid="btn-sync-destination"
                  class="mb-2 hidden"
                  severity="success"
                  @click="syncData('MAP_PACKAGE_DESTINATION')"
                />
              </div>
            </div>
          </TabPanel>
          <TabPanel :key="1" :value="1">
            <City />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </template>
  </Card>
  <Card v-if="isShowJsonFile">
    <template #title>
      <h5>{{ $t('management.json_file') }}</h5>
    </template>
    <template #content>
      <InputTextAera class="w-full" rows="24" v-model="jsonValue" />
      <div class="w-full flex justify-content-center">
        <Button data-testid="btn-save-json" :label="$t('common.save')" class="mt-2 bg-primary border-round" @click="handleUpdateFileJson(jsonValue)"></Button>
      </div>
    </template>
  </Card>
</template>
